import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Read-only markdown renderer. Uses react-markdown + remark-gfm so the
// renderer is decoupled from the editor and there's no hidden textarea
// underneath. The render output gets the `.markdown-body` class so the
// styles in index.css apply without depending on the bundled MDEditor
// theme.
//
// Code-block "Copy" buttons are rendered as a custom `pre` component so
// the imperative DOM walk that the old implementation used is gone — the
// button is just part of the React tree.
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useColorMode } from "@/hooks/useColorMode";
import { getTextDir } from "@/utils/textDir";
function extractText(node) {
    if (node === null || node === undefined || typeof node === "boolean")
        return "";
    if (typeof node === "string" || typeof node === "number")
        return String(node);
    if (Array.isArray(node))
        return node.map(extractText).join("");
    if (typeof node === "object") {
        const maybe = node;
        if (maybe.props && "children" in maybe.props)
            return extractText(maybe.props.children);
    }
    return "";
}
function CodeBlock(props) {
    const { children, className, ...rest } = props;
    const [state, setState] = useState("idle");
    async function copy() {
        const text = extractText(children).replace(/\n$/, "");
        try {
            await navigator.clipboard.writeText(text);
            setState("copied");
        }
        catch {
            setState("failed");
        }
        window.setTimeout(() => setState("idle"), 1200);
    }
    return (_jsxs("pre", { ...rest, className: className, style: { position: "relative" }, children: [_jsx("button", { type: "button", onClick: copy, className: "md-copy-btn", "aria-label": "Copy code", children: state === "copied" ? "Copied" : state === "failed" ? "Failed" : "Copy" }), children] }));
}
export function MarkdownView({ source, className, showFullPageCopy }) {
    const colorMode = useColorMode();
    const [copyState, setCopyState] = useState("idle");
    const copyFullPage = async () => {
        try {
            await navigator.clipboard.writeText(source);
            setCopyState("copied");
            window.setTimeout(() => setCopyState("idle"), 1200);
        }
        catch {
            setCopyState("failed");
            window.setTimeout(() => setCopyState("idle"), 1200);
        }
    };
    // Paragraph-level direction is handled by `unicode-bidi: plaintext` in
    // index.css; the top-level `dir` only matters for things like default
    // list-marker placement.
    const dir = getTextDir(source);
    const isRTL = dir === "rtl";
    return (_jsxs("div", { "data-color-mode": colorMode, className: `markdown-view ${className ?? ""} ${isRTL ? "dir-rtl" : ""}`, dir: isRTL ? "rtl" : undefined, children: [showFullPageCopy && (_jsxs("button", { onClick: copyFullPage, className: "mb-3 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-100 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700", children: [_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }), copyState === "copied" ? "Copied" : copyState === "failed" ? "Failed" : "Copy"] })), _jsx("div", { className: "markdown-body", children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], rehypePlugins: [rehypeRaw], components: {
                        pre: CodeBlock,
                    }, children: source }) })] }));
}
